import React, { useMemo, useRef, useState } from "react";
import {
  ClickAwayListener,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grow,
  Link,
  MenuItem,
  Paper,
  Popper,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { CustomAdvancedMenuList, CustomDialog } from "../../../components/StyledComponents";
import CustomSelect from "../../../components/CustomSelect";
import CustomDateTimePicker from "../../../components/CustomDateTimeRangePicker";
import CustomSplitButton from "../../../components/CustomSplitButton";
import ExportIcon16pxSvg from "../../../assets/images/icons/ExportIcon16pxSvg";
import {
  ControlpanelPrimaryButton,
  ControlpanelSecondaryButton,
  ControlpanelSuccess3thButton,
  GrayIconButton,
} from "../../../components/StyledButton";
import RefreshCircleIcon20pxSvgV2 from "../../../assets/images/icons/RefreshCircleIcon20pxSvgV2";
import { updateReport } from "../../../axios/Report/ReportUrlApi";
import { useAuth } from "../../../context/AuthContext";

export const getSumByOptions = (t) => [
  { value: "1", label: t("common.daily") },
  { value: "2", label: t("common.weekly") },
  { value: "4", label: t("common.monthly") },
  { value: "5", label: t("common.custom") },
];

export const getShowTimeOptions = (t) => [
  { value: "h-m-s", label: t("common.hours-minutes-seconds") },
  { value: "h-m", label: t("common.hours-minutes") },
  { value: "h", label: t("common.hours") },
];

const WEEK_DAY_NAME_TO_INDEX = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

const getReportMaxDate = () => dayjs().subtract(1, "day").endOf("day");

/** Company 5: chỉ cho cập nhật báo cáo từ tháng 10/2026 trở đi. */
const COMPANY_5_REPORT_MIN_DATE = dayjs("2026-10-01").startOf("day");

const getUpdateReportMinDate = (companyId) => {
  if (String(companyId) === "5") {
    return COMPANY_5_REPORT_MIN_DATE.toDate();
  }
  return null;
};

/**
 * Clamp khoảng ngày update report.
 * Ngoài [min, max] → kéo về ngày hiện tại (max = hôm qua).
 */
const clampUpdateReportRange = (startDate, endDate, minDate, maxDate) => {
  const max = dayjs(maxDate);
  const min = minDate ? dayjs(minDate) : null;
  const current = max;

  let start = startDate ? dayjs(startDate) : current;
  let end = endDate ? dayjs(endDate) : current;

  if (!start.isValid()) start = current;
  if (!end.isValid()) end = current;

  if (start.isAfter(max, "day") || (min && start.isBefore(min, "day"))) {
    start = current;
  }
  if (end.isAfter(max, "day") || (min && end.isBefore(min, "day"))) {
    end = current;
  }
  if (end.isBefore(start, "day")) {
    end = start;
  }

  return { start: start.toDate(), end: end.toDate() };
};

/** Weekly: chọn 1 ngày → lấy cả tuần chứa ngày đó (theo weekStartDay). */
export const getWeekRangeContaining = (date, weekStartDay) => {
  const maxDate = getReportMaxDate();
  const weekStartsOn = WEEK_DAY_NAME_TO_INDEX[weekStartDay] ?? 1;
  const picked = dayjs(date).isValid() ? dayjs(date).startOf("day") : maxDate.startOf("day");
  const diff = (picked.day() - weekStartsOn + 7) % 7;
  let start = picked.subtract(diff, "day").startOf("day");
  let end = start.add(6, "day").endOf("day");

  if (end.isAfter(maxDate, "day")) {
    end = maxDate;
  }
  if (start.isAfter(maxDate, "day")) {
    start = maxDate.startOf("day");
    end = maxDate;
  }

  return { startDate: start.toDate(), endDate: end.toDate(), anchorDate: picked.toDate() };
};

/** Monthly: chọn năm/tháng → lấy cả tháng (clamp theo ngày báo cáo tối đa = hôm qua). */
export const getMonthRangeContaining = (date) => {
  const maxDate = getReportMaxDate();
  const picked = dayjs(date).isValid() ? dayjs(date) : maxDate;
  let start = picked.startOf("month");
  let end = picked.endOf("month");

  if (end.isAfter(maxDate, "day")) {
    end = maxDate;
  }
  if (start.isAfter(maxDate, "day")) {
    start = maxDate.startOf("month");
    end = maxDate;
  }

  return { startDate: start.toDate(), endDate: end.toDate(), anchorDate: start.toDate() };
};

export const applySumByDateChange = (formData, name, value, weekStartDay) => {
  if (name === "sumBy") {
    let newStartDate = formData.startDate;
    let newEndDate = formData.endDate;
    let anchorDate = formData.anchorDate;

    if (value === "1") {
      const yesterdayDate = dayjs().subtract(1, "day").toDate();
      newStartDate = yesterdayDate;
      newEndDate = yesterdayDate;
      anchorDate = yesterdayDate;
    } else if (value === "2") {
      const week = getWeekRangeContaining(
        dayjs().subtract(1, "day").toDate(),
        weekStartDay
      );
      newStartDate = week.startDate;
      newEndDate = week.endDate;
      anchorDate = week.anchorDate;
    } else if (value === "4") {
      const month = getMonthRangeContaining(dayjs().subtract(1, "day").toDate());
      newStartDate = month.startDate;
      newEndDate = month.endDate;
      anchorDate = month.anchorDate;
    }

    return {
      ...formData,
      sumBy: value,
      startDate: newStartDate,
      endDate: newEndDate,
      anchorDate,
    };
  }

  if (name === "startDate") {
    let newStartDate = value;
    let newEndDate = formData.endDate;
    let anchorDate = value;

    if (formData.sumBy === "2") {
      const week = getWeekRangeContaining(value, weekStartDay);
      newStartDate = week.startDate;
      newEndDate = week.endDate;
      anchorDate = week.anchorDate;
    } else if (formData.sumBy === "4") {
      const month = getMonthRangeContaining(value);
      newStartDate = month.startDate;
      newEndDate = month.endDate;
      anchorDate = month.anchorDate;
    } else if (
      formData.endDate &&
      dayjs(newStartDate).isAfter(dayjs(formData.endDate), "day")
    ) {
      // Daily / Custom: nếu start > end thì kéo end theo start
      newEndDate = newStartDate;
    }

    return {
      ...formData,
      startDate: newStartDate,
      endDate: newEndDate,
      anchorDate,
    };
  }

  if (name === "endDate") {
    let newEndDate = value;
    if (
      formData.startDate &&
      dayjs(newEndDate).isBefore(dayjs(formData.startDate), "day")
    ) {
      newEndDate = formData.startDate;
    }
    return { ...formData, endDate: newEndDate };
  }

  return { ...formData, [name]: value };
};

const parseApiBody = (res) => {
  let body = res?.data;
  let depth = 0;
  while (typeof body === "string" && depth < 3) {
    const trimmed = body.trim();
    if (
      !trimmed.startsWith("{") &&
      !trimmed.startsWith("[") &&
      !trimmed.startsWith('"')
    ) {
      break;
    }
    try {
      body = JSON.parse(body);
      depth += 1;
    } catch {
      break;
    }
  }
  return body;
};

const isApiSuccess = (body) => {
  if (body === 0 || body === "0") return true;
  if (Number(body?.responseCode) === 0) return true;
  if (body?.message === "Successful" || body?.message === "Thành công") return true;
  return false;
};

const toReportDate = (date) => {
  const d = dayjs(
    date?.target?.value != null ? date.target.value : date
  );
  if (!d.isValid()) return null;
  return d.format("YYYY-MM-DD");
};

export const useReportExportMenu = (onExport) => {
  const [openExport, setOpenExport] = useState(false);
  const anchorRefExport = useRef(null);
  const { t } = useTranslation();

  const handleToggleExport = () => setOpenExport((prev) => !prev);

  const handleCloseExport = (event) => {
    if (anchorRefExport.current?.contains(event.target)) return;
    setOpenExport(false);
  };

  const handleExportExcel = () => {
    onExport();
    setOpenExport(false);
  };

  const exportMenu = (
    <Popper
      open={openExport}
      anchorEl={anchorRefExport.current}
      placement="bottom-end"
      transition
      disablePortal
      className="z-10"
    >
      {({ TransitionProps, placement }) => (
        <Grow
          {...TransitionProps}
          style={{
            transformOrigin:
              placement === "bottom" ? "left top" : "left bottom",
          }}
        >
          <Paper style={{ borderRadius: "8px", }} className="shadow-primary mt-1">
            <ClickAwayListener onClickAway={handleCloseExport}>
              <CustomAdvancedMenuList id="export-menu">
                <MenuItem disabled style={{ borderBottom: "1px solid #E5E7EB" }}>
                  <Link><p>PDF</p></Link>
                </MenuItem>
                {/* <MenuItem
                  style={{ borderBottom: "1px solid #E5E7EB" }}
                  onClick={handleExportExcel}
                >
                  <Link><p>EXCEL</p></Link>
                </MenuItem> */}
                <MenuItem disabled style={{ borderBottom: "1px solid #E5E7EB" }}>
                  <Link><p>{t("button.schedule")}</p></Link>
                </MenuItem>
                <MenuItem disabled>
                  <Link><p>{t("button.view-schedules")}</p></Link>
                </MenuItem>
              </CustomAdvancedMenuList>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  );

  return {
    anchorRefExport,
    handleToggleExport,
    handleExportExcel,
    exportMenu,
  };
};

export const ReportPageHeader = ({
  onBack,
  titleKey,
  onExport,
  startDate,
  endDate,
  onUpdateSuccess,
}) => {
  const { t } = useTranslation();
  const { isAdmin, profile } = useAuth();
  const [isUpdating, setIsUpdating] = useState(false);
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [updateStartDate, setUpdateStartDate] = useState(null);
  const [updateEndDate, setUpdateEndDate] = useState(null);
  const {
    anchorRefExport,
    handleToggleExport,
    handleExportExcel,
    exportMenu,
  } = useReportExportMenu(onExport);

  const yesterday = useMemo(
    () => dayjs().subtract(1, "day").endOf("day").toDate(),
    []
  );

  // Company 5: chỉ chọn từ 01/10/2026; ngày trước đó disable trên lịch
  const updateMinDate = useMemo(
    () => getUpdateReportMinDate(profile?.companyId),
    [profile?.companyId]
  );

  const handleOpenUpdateDialog = () => {
    if (isUpdating) return;

    // Mở từ report ngoài / ngày ngoài khoảng → kéo về ngày hiện tại
    const { start, end } = clampUpdateReportRange(
      startDate,
      endDate,
      updateMinDate,
      yesterday
    );

    setUpdateStartDate(start);
    setUpdateEndDate(end);
    setOpenUpdateDialog(true);
  };

  const handleCloseUpdateDialog = () => {
    if (isUpdating) return;
    setOpenUpdateDialog(false);
  };

  const handleUpdateRangeChange = ([start, end]) => {
    if (isUpdating) return;

    const clamped = clampUpdateReportRange(
      start,
      end,
      updateMinDate,
      yesterday
    );
    setUpdateStartDate(clamped.start);
    setUpdateEndDate(clamped.end);
  };

  const handleUpdateReport = async () => {
    if (isUpdating) return;

    const clamped = clampUpdateReportRange(
      updateStartDate,
      updateEndDate,
      updateMinDate,
      yesterday
    );
    const start = toReportDate(clamped.start);
    const end = toReportDate(clamped.end);

    if (!start || !end) {
      toast.error(t("common.select-date-range") || "Please select date range");
      return;
    }

    if (dayjs(end).isBefore(dayjs(start), "day")) {
      toast.error(t("common.invalid-date") || "Invalid date");
      return;
    }

    setIsUpdating(true);
    try {
      const response = await updateReport({
        start,
        end,
        langId: localStorage.getItem("i18nextLngId"),
      });
      const body = parseApiBody(response);

      if (isApiSuccess(body)) {
        toast.success(
          t("common.update-setting-successfully") ||
            "Update report successfully"
        );
        // Giữ dialog + loading đến khi refresh xong; không cho đóng giữa chừng
        if (onUpdateSuccess) {
          await onUpdateSuccess();
        }
        setOpenUpdateDialog(false);
      } else {
        toast.error(body?.message || t("common.something-went-wrong") || "Error");
      }
    } catch (error) {
      if (error?.response?.status !== 401) {
        const errBody = parseApiBody(error?.response);
        let message =
          (typeof errBody === "string" && errBody) ||
          errBody?.message ||
          (typeof error?.response?.data === "string"
            ? error.response.data
            : error?.response?.data?.message) ||
          t("common.error") ||
          "Error";
        // Nếu là key i18n thì dịch
        const translated = t(message);
        toast.error(translated || message);
      }
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2 min-w-0">
          <div
            onClick={onBack}
            className="flex items-center justify-center w-8 h-10 cursor-pointer select-none shrink-0"
          >
            <ArrowBackIosIcon fontSize="small" className="text-[#1F2937]" />
          </div>
          <h2 className="text-lg font-semibold text-[#1F2937] truncate">
            {t(titleKey)}
          </h2>
        </div>

        <div className="relative shrink-0 flex items-center gap-2" data-tour="reports-export-button">
          {isAdmin && (
            <ControlpanelSuccess3thButton
              onClick={handleOpenUpdateDialog}
              disabled={isUpdating}
            >
              <RefreshCircleIcon20pxSvgV2 />
              <p className="hidden ml-1 md:block">{t("button.update-data-report")}</p>
            </ControlpanelSuccess3thButton>
          )}
          <CustomSplitButton
            label={t("button.export")}
            startIcon={<ExportIcon16pxSvg color="#FCFCFC" />}
            onMainClick={handleExportExcel}
            onDropdownClick={handleToggleExport}
            ref={anchorRefExport}
          />
          {exportMenu}
        </div>
      </div>

      <CustomDialog
        maxWidth="sm"
        open={openUpdateDialog}
        onClose={handleCloseUpdateDialog}
        loading={isUpdating}
        keepMounted
      >
        <DialogTitle>
          <div className="flex justify-between items-center">
            <p className="text-xl font-semibold">
              {t("button.update-data-report")}
            </p>
            <GrayIconButton onClick={handleCloseUpdateDialog} disabled={isUpdating}>
              <CloseIcon />
            </GrayIconButton>
          </div>
        </DialogTitle>

        <DialogContent>
          <div className="pt-2 space-y-2">
            <label className="block text-sm text-[#171717]">
              {t("common.select-date-range")}
            </label>
            <CustomDateTimePicker
              mode="range"
              value={[updateStartDate, updateEndDate]}
              minDate={updateMinDate || undefined}
              maxDate={yesterday}
              onChange={handleUpdateRangeChange}
              disableTime
              showOnlyDate
            />
          </div>
        </DialogContent>

        <DialogActions>
          <div className="flex gap-2 px-4 py-4">
            <ControlpanelSecondaryButton
              onClick={handleCloseUpdateDialog}
              disabled={isUpdating}
            >
              <p>{t("button.cancel")}</p>
            </ControlpanelSecondaryButton>
            <ControlpanelPrimaryButton
              onClick={handleUpdateReport}
              disabled={isUpdating || !updateStartDate || !updateEndDate}
            >
              <p>{t("button.confirm")}</p>
            </ControlpanelPrimaryButton>
          </div>
        </DialogActions>
      </CustomDialog>
    </>
  );
};

export const ReportDateFilterRow = ({
  formData,
  setFormData,
  showTimeField = true,
}) => {
  const { t } = useTranslation();
  const { weekStartDay } = useAuth();

  const sumByOptions = useMemo(() => getSumByOptions(t), [t]);
  const showTimeOptions = useMemo(() => getShowTimeOptions(t), [t]);
  const maxDate = useMemo(
    () => new Date(new Date().setDate(new Date().getDate() - 1)),
    []
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) =>
      applySumByDateChange(prev, name, value, weekStartDay)
    );
  };

  const applyRangePick = ([start, end]) => {
    setFormData((prev) => ({
      ...prev,
      startDate: start,
      endDate: end,
      anchorDate: start,
    }));
  };

  const renderDateControl = () => {
    // Weekly: hiển thị từ ngày → đến ngày; chọn 1 ngày trong calendar → bóc cả tuần
    if (formData.sumBy === "2") {
      return (
        <CustomDateTimePicker
          mode="week"
          weekStartDay={weekStartDay}
          value={[formData.startDate, formData.endDate]}
          maxDate={maxDate}
          onChange={applyRangePick}
          disableTime
          showOnlyDate
        />
      );
    }

    // Monthly: cùng UI CustomDateTimeRangePicker; chọn năm/tháng → cả tháng, hiện khoảng từ–đến
    if (formData.sumBy === "4") {
      return (
        <CustomDateTimePicker
          mode="month"
          value={[formData.startDate, formData.endDate]}
          maxDate={maxDate}
          onChange={applyRangePick}
          disableTime
          showOnlyDate
        />
      );
    }

    // Daily / Custom: chọn khoảng từ ngày → đến ngày
    return (
      <CustomDateTimePicker
        mode="range"
        value={[formData.startDate, formData.endDate]}
        maxDate={maxDate}
        onChange={applyRangePick}
        disableTime
        showOnlyDate
      />
    );
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
      <div className="md:col-span-2">
        <CustomSelect
          className="w-full"
          name="sumBy"
          value={formData.sumBy}
          onChange={handleInputChange}
          options={sumByOptions}
        />
      </div>

      <div className="md:col-span-7">{renderDateControl()}</div>

      {showTimeField && (
        <div className="md:col-span-3">
          <CustomSelect
            className="w-full"
            name="showTime"
            value={formData.showTime}
            onChange={handleInputChange}
            options={showTimeOptions}
          />
        </div>
      )}
    </div>
  );
};
